import { tiendaDb, supabase } from '../lib/supabase';

// ─── FÓRMULAS DE MARGEN (margen SOBRE PVP, no sobre coste) ───────────────────
// PVP teórico = coste_real / (1 - margen)
// margen_real = ((pvp - coste) / pvp) * 100

export const calcularPvpTeorico = (costeReal, margenDecimal) => {
  if (!costeReal || costeReal <= 0) return null;
  if (!margenDecimal || margenDecimal <= 0 || margenDecimal >= 1) return null;
  return costeReal / (1 - margenDecimal);
};

export const calcularMargenReal = (pvpActual, costeReal) => {
  if (!pvpActual || pvpActual <= 0) return null;
  if (!costeReal || costeReal <= 0) return null;
  return ((pvpActual - costeReal) / pvpActual) * 100;
};

export const calcularCosteConImpuestos = (precioCompra, iva, recargo) => {
  if (!precioCompra || precioCompra <= 0) return null;
  const ivaVal = iva ?? 10;
  const recargoVal = recargo ?? 1.4;
  return precioCompra * (1 + ivaVal / 100 + recargoVal / 100);
};

export const calcularCosteConIva = (precioCompra, iva) => {
  if (!precioCompra || precioCompra <= 0) return null;
  return precioCompra * (1 + (iva ?? 10) / 100);
};

// Aplicar regla de redondeo psicológico
export const aplicarRedondeo = (precio, regla) => {
  if (!precio || precio <= 0) return precio;
  switch (regla) {
    case 'TERMINAR_99': {
      const base = Math.floor(precio);
      return base + 0.99;
    }
    case 'TERMINAR_95': {
      const base = Math.floor(precio);
      return base + 0.95;
    }
    case 'TERMINAR_90': {
      const base = Math.floor(precio);
      return base + 0.90;
    }
    case 'MULTIPLO_05': {
      return Math.ceil(precio / 0.05) * 0.05;
    }
    case 'MULTIPLO_10': {
      return Math.ceil(precio / 0.10) * 0.10;
    }
    case 'SIN_REDONDEO':
    default:
      return Math.round(precio * 100) / 100;
  }
};

// Calcular PVP sugerido completo (con IVA + redondeo)
export const calcularPvpSugerido = (costeReal, margenDecimal, iva, reglaRedondeo) => {
  const pvpTeorico = calcularPvpTeorico(costeReal, margenDecimal);
  if (!pvpTeorico) return null;
  // pvpTeorico es sin IVA, añadir IVA para PVP final
  const pvpConIva = pvpTeorico * (1 + (iva ?? 10) / 100);
  return aplicarRedondeo(pvpConIva, reglaRedondeo || 'TERMINAR_99');
};

// Calcular estados de calidad del producto
export const calcularEstadosProducto = (p) => {
  const estados = [];
  const coste = p?.precio_compra;
  const pvpActual = p?.pvp_venta;
  const stock = p?.stock_actual ?? p?.stock_disponible ?? 0;
  const margenObjetivo = p?.margen_objetivo ?? p?.familia_margen_objetivo;

  if (!coste || coste <= 0) estados?.push('SIN_COSTE');
  if (!p?.proveedor_principal_id) estados?.push('SIN_PROVEEDOR');
  if (!p?.familia_id) estados?.push('SIN_FAMILIA');
  if (!p?.ean) estados?.push('SIN_EAN');
  if (stock < 0) estados?.push('STOCK_NEGATIVO');
  if (coste && pvpActual && pvpActual < coste) estados?.push('PVP_BAJO_COSTE');
  if (coste && pvpActual && margenObjetivo) {
    const margenReal = calcularMargenReal(pvpActual, coste);
    if (margenReal !== null && margenReal < (margenObjetivo * 100) * 0.85) estados?.push('MARGEN_BAJO');
    if (margenReal !== null && margenReal > (margenObjetivo * 100) * 1.5) estados?.push('MARGEN_ALTO');
  }
  if (p?.bloqueo_precio) estados?.push('PRECIO_BLOQUEADO');
  if (!p?.activo) estados?.push('PRODUCTO_INACTIVO');

  return estados;
};

// ─── GENERACIÓN AUTOMÁTICA DE CÓDIGO INTERNO LDD-XXXXXX ──────────────────────
// Usa la función SQL tienda.generar_codigo_interno() que emplea una secuencia
// PostgreSQL atómica y segura ante concurrencia.
//
// tiendaDb = supabase.schema('tienda') — definido en lib/supabase.js
// tiendaDb.rpc('generar_codigo_interno') llama a tienda.generar_codigo_interno()
// correctamente porque el cliente ya tiene el header Accept-Profile: tienda.
//
// Si la función SQL falla o devuelve null/vacío: se cancela el alta.
// NO existe fallback JS: la única autoridad es tienda.seq_codigo_interno_ldd.
// BUNDLE_MARKER: 20260907-v4
export const generarCodigoInterno = async () => {
  console.log('[GENERAR CODIGO v4] inicio', new Date()?.toISOString());

  // tiendaDb ya es supabase.schema('tienda') — llama a tienda.generar_codigo_interno()
  const { data, error } = await tiendaDb?.rpc('generar_codigo_interno');

  console.log('[GENERAR CODIGO v4] data:', data);
  console.log('[GENERAR CODIGO v4] error:', error);

  if (error) {
    throw new Error(
      `No se pudo generar el código interno (error RPC): ${error?.message}. Alta cancelada.`
    );
  }

  // La función SQL devuelve TEXT. Normalizar por si Supabase lo envuelve.
  let codigoFinal = data;
  if (Array.isArray(data)) {
    codigoFinal = data?.[0];
  } else if (data && typeof data === 'object') {
    codigoFinal = Object.values(data)?.[0];
  }

  if (!codigoFinal || typeof codigoFinal !== 'string' || !codigoFinal?.startsWith('LDD-')) {
    throw new Error(
      `La función tienda.generar_codigo_interno() devolvió un valor inválido: "${codigoFinal}" (raw: ${JSON.stringify(data)}). Alta cancelada.`
    );
  }

  return codigoFinal;
};

// ─── DETECCIÓN DE DUPLICADOS ──────────────────────────────────────────────────
export const buscarDuplicados = async ({ codigoInterno, plu, ean, referenciaProveedor, excludeId }) => {
  const candidatos = [];

  // Buscar por código interno
  if (codigoInterno?.trim()) {
    const { data } = await tiendaDb?.from('productos')
      ?.select('id, nombre, codigo_interno, ean, plu, codigo_epelsa')
      ?.eq('codigo_interno', codigoInterno?.trim())
      ?.limit(3);
    (data || [])?.forEach(p => {
      if (!excludeId || p?.id !== excludeId) candidatos?.push({ ...p, motivo: 'Código interno coincide' });
    });
  }

  // Buscar por PLU
  if (plu?.trim()) {
    const { data } = await tiendaDb?.from('productos')
      ?.select('id, nombre, codigo_interno, ean, plu, codigo_epelsa')
      ?.eq('plu', plu?.trim())
      ?.limit(3);
    (data || [])?.forEach(p => {
      if (!excludeId || p?.id !== excludeId) {
        if (!candidatos?.find(c => c?.id === p?.id)) candidatos?.push({ ...p, motivo: 'PLU coincide' });
      }
    });
  }

  // Buscar por EAN
  if (ean?.trim()) {
    const { data } = await tiendaDb?.from('productos')
      ?.select('id, nombre, codigo_interno, ean, plu, codigo_epelsa')
      ?.eq('ean', ean?.trim())
      ?.limit(3);
    (data || [])?.forEach(p => {
      if (!excludeId || p?.id !== excludeId) {
        if (!candidatos?.find(c => c?.id === p?.id)) candidatos?.push({ ...p, motivo: 'EAN coincide' });
      }
    });
  }

  // Buscar por referencia proveedor
  if (referenciaProveedor?.trim()) {
    const { data } = await tiendaDb?.from('productos')
      ?.select('id, nombre, codigo_interno, ean, plu, codigo_epelsa')
      ?.eq('referencia_proveedor', referenciaProveedor?.trim())
      ?.limit(3);
    (data || [])?.forEach(p => {
      if (!excludeId || p?.id !== excludeId) {
        if (!candidatos?.find(c => c?.id === p?.id)) candidatos?.push({ ...p, motivo: 'Referencia proveedor coincide' });
      }
    });
  }

  return candidatos;
};

// ─── SERVICIO PRODUCTOS ───────────────────────────────────────────────────────
export const productosService = {
  // Obtener todos los productos con joins (performance-optimized via RPC)
  async getAll() {
    const { data, error } = await tiendaDb?.rpc('get_productos_catalogo');
    if (error) {
      // Fallback a query directa si RPC no está disponible
      const { data: fallback, error: err2 } = await tiendaDb
        ?.from('productos')
        ?.select(`
          *,
          familia:familias_producto(id, nombre, margen_objetivo, regla_redondeo),
          proveedor:proveedores(id, nombre),
          inventario:inventario_actual(stock_actual, stock_disponible)
        `)
        ?.order('nombre');
      if (err2) throw err2;
      return (fallback || [])?.map(p => ({
        ...p,
        familia_nombre: p?.familia?.nombre,
        familia_margen_objetivo: p?.familia?.margen_objetivo,
        familia_regla_redondeo: p?.familia?.regla_redondeo,
        proveedor_nombre: p?.proveedor?.nombre,
        stock_actual: p?.inventario?.[0]?.stock_actual ?? 0,
        stock_disponible: p?.inventario?.[0]?.stock_disponible ?? 0,
      }));
    }
    return data || [];
  },

  // Guardar producto (create o update) — marca PENDIENTE_ALTA/PENDIENTE_ACTUALIZACION en cambios relevantes
  async save(payload) {
    const { id, familia_nombre, familia_margen_objetivo, familia_regla_redondeo,
      familia_iva_defecto, familia_recargo_defecto, proveedor_nombre,
      stock_actual, stock_disponible, familia, proveedor, inventario, ...rest } = payload;

    const CAMPOS_EPELSA = ['nombre', 'plu', 'ean', 'tipo_iva', 'unidad_venta', 'pvp_venta', 'activo', 'familia_id', 'codigo_epelsa'];
    const dbPayload = { ...rest, updated_at: new Date()?.toISOString() };

    if (id) {
      // Verificar si algún campo relevante para Epelsa cambió
      const camposModificados = CAMPOS_EPELSA?.some(c => rest?.[c] !== undefined);
      if (camposModificados && !dbPayload?.epelsa_sync_status) {
        dbPayload.epelsa_sync_status = 'PENDIENTE_ACTUALIZACION';
      }
      const { error } = await tiendaDb?.from('productos')?.update(dbPayload)?.eq('id', id);
      if (error) throw error;

      // Encolar en cola_sync_productos_epelsa si hay cambios relevantes
      if (camposModificados) {
        await tiendaDb?.from('cola_sync_productos_epelsa')?.insert({
          producto_id: id,
          codigo_epelsa: rest?.codigo_epelsa || null,
          plu: rest?.plu || null,
          ean: rest?.ean || null,
          nombre: rest?.nombre || null,
          pvp: rest?.pvp_venta || null,
          tipo_iva: rest?.tipo_iva || null,
          unidad_venta: rest?.unidad_venta || null,
          activo: rest?.activo ?? true,
          tipo_operacion: 'ACTUALIZACION',
          estado: 'PENDIENTE',
          origen: 'MANUAL',
        });
      }
    } else {
      // Nuevo producto → generar codigo_interno si está vacío
      if (!dbPayload?.codigo_interno?.trim()) {
        dbPayload.codigo_interno = await generarCodigoInterno();
      }
      // Guardia explícita: nunca insertar sin codigo_interno
      if (!dbPayload?.codigo_interno || typeof dbPayload?.codigo_interno !== 'string' || !dbPayload?.codigo_interno?.startsWith('LDD-')) {
        throw new Error(
          `codigo_interno inválido o no generado: "${dbPayload?.codigo_interno}". El INSERT ha sido cancelado.`
        );
      }
      // Siempre PENDIENTE_ALTA para nuevos productos
      dbPayload.epelsa_sync_status = 'PENDIENTE_ALTA';
      const { data: newProd, error } = await tiendaDb?.from('productos')?.insert(dbPayload)?.select('id')?.single();
      if (error) throw error;

      // Encolar alta en cola_sync_productos_epelsa
      if (newProd?.id) {
        await tiendaDb?.from('cola_sync_productos_epelsa')?.insert({
          producto_id: newProd?.id,
          codigo_epelsa: dbPayload?.codigo_epelsa || null,
          plu: dbPayload?.plu || null,
          ean: dbPayload?.ean || null,
          nombre: dbPayload?.nombre || null,
          pvp: dbPayload?.pvp_venta || null,
          tipo_iva: dbPayload?.tipo_iva || null,
          unidad_venta: dbPayload?.unidad_venta || null,
          activo: dbPayload?.activo ?? true,
          tipo_operacion: 'ALTA',
          estado: 'PENDIENTE',
          origen: 'MANUAL',
        });
      }
      return { id: newProd?.id, codigo_interno: dbPayload?.codigo_interno };
    }
  },

  // Crear producto desde una línea de factura y vincular automáticamente
  async crearDesdeFactura({ productoPayload, lineaId, facturaId, proveedorId, precioSinIva, iva, recargo, descuentoPct }) {
    // Generar codigo_interno automáticamente — única autoridad: tienda.generar_codigo_interno()
    // Si falla, lanza excepción y cancela el alta (sin fallback JS)
    // IMPORTANTE: NO leer codigo_interno de productoPayload — puede ser null/undefined
    // del formulario y sobrescribiría el valor generado si se pusiera antes del spread.
    const codigoInterno = await generarCodigoInterno();

    // Guardia explícita: nunca insertar sin codigo_interno
    if (
      !codigoInterno ||
      typeof codigoInterno !== 'string' || !codigoInterno?.startsWith('LDD-')
    ) {
      throw new Error(
        `No se pudo generar codigo_interno. Valor recibido: ${String(codigoInterno)}. El INSERT ha sido cancelado.`
      );
    }

    // Eliminar codigo_interno de productoPayload para evitar que un null/undefined
    // del formulario sobrescriba el valor generado por la secuencia SQL.
    const { codigo_interno: _descartado, ...productoPayloadLimpio } = productoPayload || {};

    // 1. Crear el producto con estado PENDIENTE_ALTA (nuevo producto desde factura)
    // CRÍTICO: codigo_interno se añade DESPUÉS del spread para que nunca sea sobrescrito.
    const payload = {
      ...productoPayloadLimpio,
      codigo_interno: codigoInterno,   // explícito — siempre después del spread
      epelsa_sync_status: 'PENDIENTE_ALTA',
      created_at: new Date()?.toISOString(),
      updated_at: new Date()?.toISOString(),
    };

    console.log('[INSERT REAL PRODUCTO]', payload);
    console.log('[CREAR PRODUCTO v4] codigoInterno:', codigoInterno);
    console.log('[CREAR PRODUCTO v4] payload final:', payload);

    const { data: newProd, error: errCreate } = await tiendaDb
      ?.from('productos')
      ?.insert(payload)
      ?.select('id')
      ?.single();
    if (errCreate) throw errCreate;

    const productoId = newProd?.id;

    // 2. Vincular la línea de factura al nuevo producto
    if (lineaId) {
      await tiendaDb?.from('lineas_factura_compra')?.update({
        producto_id: productoId,
        estado_vinculacion: 'VINCULADO',
        metodo_vinculacion: 'CREADO_DESDE_FACTURA',
        confianza_vinculacion: 100,
      })?.eq('id', lineaId);
    }

    // 3. Guardar equivalencia proveedor-producto para aprendizaje futuro
    if (proveedorId && productoPayloadLimpio?.referencia_proveedor) {
      await tiendaDb?.from('ocr_product_mappings')?.upsert({
        producto_id: productoId,
        proveedor_id: proveedorId,
        referencia_proveedor: productoPayloadLimpio?.referencia_proveedor,
        descripcion_proveedor: productoPayloadLimpio?.descripcion_proveedor || productoPayloadLimpio?.nombre,
        ean_proveedor: productoPayloadLimpio?.ean || null,
        confianza: 100,
        veces_confirmado: 1,
        metodo_vinculacion: 'CREADO_DESDE_FACTURA',
        activo: true,
        updated_at: new Date()?.toISOString(),
      }, { onConflict: 'producto_id,proveedor_id,referencia_proveedor', ignoreDuplicates: false });
    }

    // 4. Registrar coste inicial si hay precio
    if (precioSinIva && precioSinIva > 0) {
      const ivaVal = iva ?? 10;
      const recargoVal = recargo ?? 1.4;
      const descuento = descuentoPct ?? 0;
      const precioNeto = precioSinIva * (1 - descuento / 100);
      const precioConIva = precioNeto * (1 + ivaVal / 100);
      const costeConImpuestos = precioNeto * (1 + ivaVal / 100 + recargoVal / 100);

      await tiendaDb?.from('historico_precios_compra')?.insert({
        producto_id: productoId,
        proveedor_id: proveedorId || null,
        factura_compra_id: facturaId || null,
        linea_factura_compra_id: lineaId || null,
        fecha: new Date()?.toISOString()?.split('T')?.[0],
        precio_bruto: precioSinIva,
        descuento_porcentaje: descuento,
        precio_neto: precioNeto,
        iva_porcentaje: ivaVal,
        recargo_equivalencia_pct: recargoVal,
        coste_con_impuestos: costeConImpuestos,
        pvp_actual: productoPayloadLimpio?.pvp_venta || null,
        pvp_sugerido: productoPayloadLimpio?.pvp_sugerido || null,
        margen_aplicado: productoPayloadLimpio?.margen_objetivo || null,
        origen: 'COMPRA',
      });

      // Actualizar coste en el producto recién creado
      await tiendaDb?.from('productos')?.update({
        precio_compra: precioNeto,
        precio_compra_con_iva: precioConIva,
        coste_con_impuestos: costeConImpuestos,
        updated_at: new Date()?.toISOString(),
      })?.eq('id', productoId);
    }

    // 5. Encolar en cola_sync_productos_epelsa para alta futura en Epelsa
    await tiendaDb?.from('cola_sync_productos_epelsa')?.insert({
      producto_id: productoId,
      codigo_epelsa: productoPayloadLimpio?.codigo_epelsa || null,
      plu: productoPayloadLimpio?.plu || null,
      ean: productoPayloadLimpio?.ean || null,
      nombre: productoPayloadLimpio?.nombre || null,
      pvp: productoPayloadLimpio?.pvp_venta || null,
      tipo_iva: productoPayloadLimpio?.tipo_iva || null,
      unidad_venta: productoPayloadLimpio?.unidad_venta || null,
      activo: productoPayloadLimpio?.activo ?? true,
      tipo_operacion: 'ALTA',
      estado: 'PENDIENTE',
      origen: 'FACTURA_OCR',
      factura_id: facturaId || null,
      linea_factura_id: lineaId || null,
      proveedor_id: proveedorId || null,
      referencia_proveedor: productoPayloadLimpio?.referencia_proveedor || null,
    });

    // Retornar id y codigo_interno generado para mostrarlo al usuario
    return { id: productoId, codigo_interno: codigoInterno };
  },

  // Actualización masiva
  async bulkUpdate(ids, updates) {
    const errors = [];
    for (const id of ids) {
      const { error } = await tiendaDb?.from('productos')
        ?.update({ ...updates, updated_at: new Date()?.toISOString() })
        ?.eq('id', id);
      if (error) errors?.push({ id, error: error?.message });
    }
    if (errors?.length > 0) throw new Error(`Errores en ${errors.length} productos`);
  },

  // Guardar histórico de precio de compra
  async guardarHistoricoCoste(productoId, datos) {
    const { error } = await tiendaDb?.from('historico_precios_compra')?.insert({
      producto_id: productoId,
      proveedor_id: datos?.proveedor_id || null,
      factura_compra_id: datos?.factura_id || null,
      fecha: datos?.fecha || new Date()?.toISOString()?.split('T')?.[0],
      precio_bruto: datos?.precio_sin_iva || 0,
      precio_neto: datos?.precio_sin_iva || 0,
      iva_porcentaje: datos?.iva || 10,
      recargo_equivalencia_pct: datos?.recargo || 1.4,
      coste_con_impuestos: datos?.coste_con_impuestos || 0,
      pvp_sugerido: datos?.pvp_sugerido || null,
      pvp_actual: datos?.pvp_actual || null,
      margen_aplicado: datos?.margen_aplicado || null,
    });
    if (error) throw error;
  },

  // Obtener histórico de costes de un producto
  async getHistoricoCoste(productoId) {
    const { data, error } = await tiendaDb
      ?.from('historico_precios_compra')
      ?.select(`*, proveedor:proveedores(nombre)`)
      ?.eq('producto_id', productoId)
      ?.order('fecha', { ascending: false })
      ?.limit(50);
    if (error) throw error;
    return data || [];
  },

  // ─── ACTUALIZAR COSTE DESDE FACTURA (seguro, con histórico) ──────────────────
  async actualizarCosteDesdeFactura({
    productoId,
    precioSinIva,
    iva,
    recargo,
    descuentoPct,
    proveedorId,
    facturaId,
    lineaId,
  }) {
    // Obtener datos actuales del producto
    const { data: producto, error: errProd } = await tiendaDb
      ?.from('productos')
      ?.select(
        'id, precio_compra, pvp_venta, pvp_sugerido, margen_objetivo, tipo_iva, recargo_equivalencia, bloqueo_precio, proveedor_principal_id'
      )
      ?.eq('id', productoId)
      ?.single();
    if (errProd) throw errProd;

    const descuento = descuentoPct ?? 0;
    const ivaVal = iva ?? producto?.tipo_iva ?? 10;
    const recargoVal = recargo ?? producto?.recargo_equivalencia ?? 1.4;
    const precioNeto = precioSinIva * (1 - descuento / 100);
    const precioConIva = precioNeto * (1 + ivaVal / 100);
    const costeConImpuestos = precioNeto * (1 + ivaVal / 100 + recargoVal / 100);

    const costoAnterior = producto?.precio_compra || null;

    // Guardar histórico
    await tiendaDb?.from('historico_precios_compra')?.insert({
      producto_id: productoId,
      proveedor_id: proveedorId || null,
      factura_compra_id: facturaId || null,
      linea_factura_compra_id: lineaId || null,
      fecha: new Date()?.toISOString()?.split('T')?.[0],
      precio_bruto: precioSinIva,
      descuento_porcentaje: descuento,
      precio_neto: precioNeto,
      iva_porcentaje: ivaVal,
      recargo_equivalencia_pct: recargoVal,
      coste_con_impuestos: costeConImpuestos,
      pvp_actual: producto?.pvp_venta || null,
      pvp_sugerido: producto?.pvp_sugerido || null,
      margen_aplicado: producto?.margen_objetivo || null,
    });

    // Calcular si hay que marcar precio_revisar
    const margenObjetivo = (producto?.margen_objetivo ?? 35) / 100;
    const pvpActual = producto?.pvp_venta || 0;
    let precioRevisar = false;
    let precioRevisarMotivo = null;

    if (pvpActual > 0 && costeConImpuestos > 0) {
      const margenActual = (pvpActual - costeConImpuestos) / pvpActual;
      const margenMinimo = margenObjetivo * 0.85;
      if (margenActual < margenMinimo) {
        precioRevisar = true;
        precioRevisarMotivo = `Margen actual ${(margenActual * 100)?.toFixed(1)}% < mínimo ${(margenMinimo * 100)?.toFixed(1)}%`;
      }
      if (costoAnterior && costoAnterior > 0) {
        const variacion = (costeConImpuestos - costoAnterior) / costoAnterior;
        if (variacion > 0.05) {
          precioRevisar = true;
          precioRevisarMotivo = `Coste subió ${(variacion * 100)?.toFixed(1)}%`;
        }
      }
    }

    const updatePayload = {
      precio_compra: precioNeto,
      precio_compra_con_iva: precioConIva,
      coste_con_impuestos: costeConImpuestos,
      coste_anterior: costoAnterior,
      precio_revisar: precioRevisar,
      precio_revisar_motivo: precioRevisarMotivo,
      updated_at: new Date()?.toISOString(),
    };

    if (!producto?.proveedor_principal_id && proveedorId) {
      updatePayload.proveedor_principal_id = proveedorId;
    }

    const { error } = await tiendaDb?.from('productos')?.update(updatePayload)?.eq('id', productoId);
    if (error) throw error;

    return { costoAnterior, costoNuevo: precioNeto, costeConImpuestos, precioRevisar };
  },
};

export default productosService;

// ─── SERVICIO OFICIAL SALA CORTE — public.productos ──────────────────────────
// Este servicio gestiona el catálogo operativo de Sala Corte (public.productos).
// NO confundir con productosService que gestiona tienda.productos (Tienda General).
export const productService = {
  async getAll() {
    const { data, error } = await supabase
      ?.from('productos')
      ?.select('id, nombre, categoria, activo, created_at')
      ?.order('nombre', { ascending: true });
    if (error) throw error;
    return data || [];
  },

  async getById(id) {
    const { data, error } = await supabase
      ?.from('productos')
      ?.select('id, nombre, categoria, activo, created_at')
      ?.eq('id', id)
      ?.single();
    if (error) throw error;
    return data;
  },

  async create(payload) {
    const { data, error } = await supabase
      ?.from('productos')
      ?.insert({ nombre: payload?.nombre, categoria: payload?.categoria || null, activo: payload?.activo ?? true })
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async update(id, payload) {
    const { nombre, categoria, activo } = payload;
    const updates = {};
    if (nombre !== undefined) updates.nombre = nombre;
    if (categoria !== undefined) updates.categoria = categoria;
    if (activo !== undefined) updates.activo = activo;
    const { data, error } = await supabase
      ?.from('productos')
      ?.update(updates)
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  async delete(id) {
    const { error } = await supabase?.from('productos')?.delete()?.eq('id', id);
    if (error) throw error;
  },
};

